import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, Volume2, X } from 'lucide-react';
import { primaryBtn, secondaryBtn } from './engineTheme';
import { trackMiniAppEvent } from '../../../utils/analytics';
import { GUIDED_HINT_WRONG_BEFORE_EXPLANATION } from './guidedHintModel';

const levelLabel = (level) => {
  if (level <= 1) return 'Gentle nudge';
  if (level === 2) return 'Concept clue';
  return 'More direct';
};

export function GuidedHintHelpButton({ onClick, visible, label = 'Hint help' }) {
  if (!visible) return null;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        position: 'fixed',
        zIndex: 190,
        right: 14,
        bottom: 'calc(76px + env(safe-area-inset-bottom, 0px))',
        width: 48,
        height: 48,
        borderRadius: 999,
        border: '2px solid var(--color-primary)',
        background: 'var(--color-bg-card)',
        color: 'var(--color-primary)',
        boxShadow: '0 6px 22px var(--color-shadow)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      <HelpCircle size={26} strokeWidth={2.2} aria-hidden />
    </button>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {boolean} props.isNarrow
 * @param {number} props.visibleLevel
 * @param {number} props.maxLevel
 * @param {boolean} [props.isInteractionPhase] — “how to use” one-shot (not counted in content levels)
 * @param {boolean} [props.anotherClueEnabled] — override for Another clue button
 * @param {{ text: string, audioText: string } | null} props.hintEntry
 * @param {boolean} props.showHowAvailable
 * @param {string} props.resolvedExplanation
 * @param {boolean} props.explanationOpen
 * @param {() => void} props.onDismiss
 * @param {() => void} props.onAnotherClue
 * @param {() => void} props.onReadAloud
 * @param {() => void} props.onShowHow
 */
export default function GuidedHintOverlay({
  open,
  isNarrow,
  visibleLevel,
  maxLevel,
  isInteractionPhase = false,
  anotherClueEnabled,
  hintEntry,
  showHowAvailable,
  resolvedExplanation,
  explanationOpen,
  onDismiss,
  onAnotherClue,
  onReadAloud,
  onShowHow,
}) {
  useEffect(() => {
    if (!open && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, [open]);

  if (!open || !hintEntry) return null;

  const canAnother =
    typeof anotherClueEnabled === 'boolean' ? anotherClueEnabled : visibleLevel < maxLevel;
  const ttsSupported =
    typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined';

  const bodyStyle = {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: 600,
    color: 'var(--color-text)',
  };

  const chipStyle = {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--color-primary)',
    marginBottom: 8,
  };

  const panelInner = (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={chipStyle}>
            {isInteractionPhase ? (
              <>How to use · tap / drag tips (once)</>
            ) : (
              <>
                Hint · {levelLabel(visibleLevel)} ({visibleLevel}/{maxLevel})
              </>
            )}
          </div>
          <p style={bodyStyle}>{hintEntry.text}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close hint"
          style={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: 12,
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={22} aria-hidden />
        </button>
      </div>

      {showHowAvailable ? (
        <div style={{ marginTop: 14 }}>
          {!explanationOpen ? (
            <button
              type="button"
              style={{
                ...secondaryBtn,
                width: '100%',
                minHeight: 46,
                borderStyle: 'solid',
                borderColor: 'var(--color-accent, rgba(251, 191, 36, 0.95))',
                background: 'rgba(251, 191, 36, 0.1)',
              }}
              onClick={onShowHow}
            >
              Show me how ({GUIDED_HINT_WRONG_BEFORE_EXPLANATION}+ tries)
            </button>
          ) : (
            <div
              role="region"
              aria-label="Guided explanation"
              style={{
                padding: '12px 14px',
                borderRadius: 14,
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                fontSize: 15,
                lineHeight: 1.45,
                fontWeight: 600,
              }}
            >
              {resolvedExplanation}
            </div>
          )}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexDirection: isNarrow ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: 10,
          marginTop: 16,
        }}
      >
        <button type="button" style={{ ...primaryBtn, flex: isNarrow ? '1 1 auto' : '1 1 120px' }} onClick={onDismiss}>
          Got it
        </button>
        <button
          type="button"
          style={{
            ...secondaryBtn,
            flex: isNarrow ? '1 1 auto' : '1 1 120px',
            opacity: canAnother ? 1 : 0.45,
          }}
          onClick={onAnotherClue}
          disabled={!canAnother}
        >
          Another clue
        </button>
        {ttsSupported ? (
          <button
            type="button"
            style={{
              ...secondaryBtn,
              flex: isNarrow ? '1 1 auto' : '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
            onClick={onReadAloud}
          >
            <Volume2 size={18} aria-hidden />
            Read aloud
          </button>
        ) : null}
      </div>
    </>
  );

  if (isNarrow) {
    const mobileRoot = typeof document !== 'undefined' ? document.body : null;
    if (!mobileRoot) return null;
    return createPortal(
      <>
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 178,
            background: 'rgba(15, 23, 42, 0.28)',
          }}
          onClick={onDismiss}
          onKeyDown={(e) => e.key === 'Escape' && onDismiss()}
        />
        <div
          className="hide-scrollbar"
          role="dialog"
          aria-modal="true"
          aria-label="Activity hint"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 180,
            maxHeight: 'min(42vh, 320px)',
            overflowY: 'auto',
            padding: '16px 16px calc(18px + env(safe-area-inset-bottom, 0px))',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            background: 'var(--color-bg-card)',
            borderTop: '2px solid var(--color-border)',
            boxShadow: '0 -12px 40px rgba(0,0,0,0.18)',
            boxSizing: 'border-box',
          }}
        >
          {panelInner}
        </div>
      </>,
      mobileRoot,
    );
  }

  return (
    <div
      className="hide-scrollbar"
      role="dialog"
      aria-modal="false"
      aria-label="Activity hint"
      style={{
        position: 'absolute',
        zIndex: 45,
        right: 8,
        bottom: 8,
        top: 'auto',
        width: 'min(360px, calc(100% - 16px))',
        maxWidth: '100%',
        maxHeight: 'min(42vh, 280px)',
        overflowY: 'auto',
        padding: 14,
        borderRadius: 16,
        background: 'var(--color-bg-card)',
        border: '2px solid var(--color-border)',
        boxShadow: '0 10px 36px var(--color-shadow)',
        boxSizing: 'border-box',
      }}
    >
      {panelInner}
    </div>
  );
}

export function guidedHintReadAloud(hintEntry, activityId, visibleLevel) {
  const text = (hintEntry?.audioText || hintEntry?.text || '').trim();
  if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  window.speechSynthesis.speak(u);
  trackMiniAppEvent.hintAudioPlayed(activityId, { level: visibleLevel });
}
